import { deleteSessionDataAtom } from '@/components/modal/delete-session-modal.atoms';
import {
  clearAllSelectionsAtom,
  hasSelectedSessionsAtom,
  selectedSessionIdsAtom,
  selectedSessionsCountAtom,
} from '@/features/consolidatedSessionsList/atoms';
import { consolidatedSessionsListMessages } from '@/features/consolidatedSessionsList/consolidatedSessionsList.messages';
import { isMac } from '@/hooks/platform';
import { Button } from '@heidi/design-system/button';
import { Command, CommandItem, CommandList } from '@heidi/design-system/command';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { useAtomValue, useSetAtom } from 'jotai';
import { Trash, X } from 'lucide-react';
import { memo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

const SessionSelectionBar = memo(() => {
  const intl = useIntl();
  const hasSelectedSessions = useAtomValue(hasSelectedSessionsAtom);
  const selectedSessionsCount = useAtomValue(selectedSessionsCountAtom);
  const clearAllSelections = useSetAtom(clearAllSelectionsAtom);
  const selectedSessionIds = useAtomValue(selectedSessionIdsAtom);
  const setDeleteSessionData = useSetAtom(deleteSessionDataAtom);

  const handleDeleteClick = useCallback(() => {
    const ids = [...selectedSessionIds];
    if (ids.length === 0) return;
    setDeleteSessionData({
      sessionsToDelete: ids,
      selectedAll: false,
      onSessionsDeleted: clearAllSelections,
    });
  }, [selectedSessionIds, setDeleteSessionData, clearAllSelections]);

  if (!hasSelectedSessions) return null;

  return (
    <div className="absolute inset-x-0 bottom-2 z-50 flex w-full justify-center duration-200 ease-out animate-in slide-in-from-bottom-2">
      <div className="flex w-fit justify-center gap-2">
        <Button
          className="bg-surface"
          variant="outline"
          size="xs"
          onClick={clearAllSelections}
        >
          {intl.formatMessage(consolidatedSessionsListMessages.nSelected, {
            count: selectedSessionsCount,
          })}
          <X />
        </Button>
        <Popover>
          <PopoverTrigger asChild>
            <Button className="gap-2 bg-surface-accent" variant="outline" size="xs">
              {isMac ? '⌘' : 'Ctrl'}
              <FormattedMessage {...consolidatedSessionsListMessages.actions} />
            </Button>
          </PopoverTrigger>
          <PopoverContent side="top" align="center" className="w-48 p-1">
            <Command>
              <CommandList>
                <CommandItem className="gap-2 bg-surface" onSelect={handleDeleteClick}>
                  <Trash size={16} />
                  <FormattedMessage {...consolidatedSessionsListMessages.delete} />
                </CommandItem>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
});

SessionSelectionBar.displayName = 'SessionSelectionBar';
export default SessionSelectionBar;
